import React from 'react';
import { formatDate } from '../../utils/dateUtils';
import { InfoCircledIcon } from '@radix-ui/react-icons';

interface Column {
  class: string;
  header: string;
  key: string;
  show: number;
  show_as_info?: number;
}

interface Row {
  [key: string]: any;
}

interface DynamicTableProps {
  columns: Column[];
  rows: Row[];
  selectedDocuments: string[];
  onSelectDocument: (id: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onDocumentClick: (docId: string, type: string) => void;
  onInfoClick?: (event: React.MouseEvent, rowId: string) => void;
  onInfoMouseLeave?: () => void;
  isRowExpanded?: (rowId: string) => boolean;
  renderDrillDownContent?: (rowId: string, type: string) => React.ReactNode;
  onDownloadExcel?: (vatEntryId: string, weekEnding: string | null) => void;
  showDrillDown?: boolean;
  showCheckboxes?: boolean;
}

const DynamicTable: React.FC<DynamicTableProps> = ({
  columns,
  rows,
  selectedDocuments,
  onSelectDocument,
  onSelectAll,
  onDocumentClick,
  onInfoClick,
  onInfoMouseLeave,
  isRowExpanded,
  renderDrillDownContent,
  onDownloadExcel,
  showDrillDown = true,
  showCheckboxes = true
}) => {
  // Filter columns to only show those with show: 1
  const visibleColumns = columns.filter(col => col.show === 1);
  
  // Check if any rows are VAT books to determine if we should show expand/collapse
  const hasVatBooks = rows.some(row => {
    if (row.document_type) return row.document_type === 'vat_book';
    return false;
  });
  
  // Get document type from the parent component or determine from data structure
  const getDocumentType = (row: Row) => {
    // Check if the row has a document_type field
    if (row.document_type) {
      console.log('Using document_type from row:', row.document_type);
      return row.document_type;
    }
    
    // If no document_type field, try to infer from other fields or row structure
    // This is a fallback - ideally the API should provide document_type
    if (row.invoice_number || row.invoice_id) {
      console.log('Inferring invoice type from row fields');
      return 'invoice';
    }
    if (row.cashbook_id || row.cashbook_number) {
      console.log('Inferring cashbook type from row fields');
      return 'cashbook';
    }
    if (row.vat_book_id || row.vat_book_number) {
      console.log('Inferring vat_book type from row fields');
      return 'vat_book';
    }
    if (row.bank_statement_id || row.statement_number) {
      console.log('Inferring bank_statement type from row fields');
      return 'bank_statement';
    }
    
    // Default fallback
    console.log('Using default document type, row keys:', Object.keys(row));
    return 'document';
  };
  
  // Format cell value
  const formatCellValue = (value: any, key: string, row: Row, documentType: string) => {
    if (value === null || value === undefined) return '-';
    
    // Handle actions column
    if (key === 'actions') {
      if (documentType === 'vat_book' && value && value.download) {
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onDownloadExcel) {
                onDownloadExcel(row.id.toString(), row.week_ending);
              }
            }}
            className="px-3 py-1 rounded-md bg-primary text-white hover:bg-primary/90 dark:bg-blue-600 dark:hover:bg-blue-700 text-sm"
          >
            Download
          </button>
        );
      }
      return '-';
    }
    
    // Handle info column with comments
    if (key === 'info' && value && Array.isArray(value) && value.length > 0) {
      return (
        <div className="flex items-center justify-center">
          <div
            onMouseEnter={(e) => {
              e.stopPropagation();
              if (onInfoClick) {
                onInfoClick(e, row.id);
              }
            }}
            onMouseLeave={(e) => {
              e.stopPropagation();
              if (onInfoMouseLeave) {
                onInfoMouseLeave();
              }
            }}
            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group cursor-pointer"
            title="Hover to view comments"
          >
            <InfoCircledIcon className="w-5 h-5 text-blue-500 group-hover:text-blue-600" />
          </div>
        </div>
      );
    }
    
    // Format currency for amount fields
    // if (key.includes('amount') || key.includes('price') || key.includes('worth') || key.includes('gross') || key.includes('subtotal') || key.includes('vat_amount') || key.includes('cis')) {
    //   if (typeof value === 'number') {
    //     return new Intl.NumberFormat('en-US', {
    //       style: 'currency',
    //       currency: 'USD',
    //       minimumFractionDigits: 2
    //     }).format(value);
    //   }
    // }
    
    // Format dates
    if (key.includes('date') && value) {
      return formatDate(value);
    }
    
    return value.toString();
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            {showCheckboxes && (
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-100 dark:bg-gray-700 cursor-default"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                  checked={rows.length > 0 && selectedDocuments.length === rows.length}
                  onChange={(e) => {
                    e.stopPropagation();
                    onSelectAll(e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </th>
            )}
            {!hasVatBooks && showDrillDown && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {/* Expand/Collapse column */}
              </th>
            )}
            {visibleColumns.map((column) => (
              <th
                key={column.key}
                className={`px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider ${column.class}`}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          {rows.length === 0 ? (
            <tr>
                          <td 
              colSpan={visibleColumns.length + (hasVatBooks ? 1 : 2) + (showCheckboxes ? 1 : 0)} 
              className="px-6 py-12 text-center text-gray-500 dark:text-gray-400"
            >
                <div className="flex flex-col items-center justify-center">
                  <div className="text-lg font-medium mb-2">No data available</div>
                  <div className="text-sm">Try adjusting your filters or upload new documents</div>
                </div>
              </td>
            </tr>
          ) : (
            rows.map((row, index) => {
              console.log('Row data:', row);
              
              // Ensure we have a valid row ID - this should be the actual row ID (invoice ID, cashbook ID, etc.)
              if (!row.id) {
                console.error('Row missing ID:', row);
                return null; // Skip rows without IDs
              }
              
              const rowId = row.id; // Use the actual row ID
              const docId = row.document_id; // Use document_id for navigation
              
              console.log('Row ID (for selection):', rowId);
              console.log('Document ID (for navigation):', docId);
              
              const documentType = getDocumentType(row);
              console.log('Document type for row:', documentType);
              const isSelected = selectedDocuments.includes(rowId);
              const isExpanded = isRowExpanded ? isRowExpanded(rowId) : false;
              
              return (
                <React.Fragment key={rowId}>
                  <tr
                    className={`hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer ${
                      isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    } ${isExpanded ? 'bg-gray-100 dark:bg-gray-800' : ''}`}
                    onClick={() => onDocumentClick(rowId, documentType)}
                  >
                    {showCheckboxes && (
                      <td 
                        className="px-6 py-4 whitespace-nowrap bg-gray-50 dark:bg-gray-800 cursor-default hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            console.log('Checkbox changed - Row ID:', rowId, 'Checked:', e.target.checked);
                            onSelectDocument(rowId, e.target.checked);
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        />
                      </td>
                    )}
                    {!hasVatBooks && showDrillDown && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        <button
                          className="text-primary hover:text-primary/80 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDocumentClick(rowId, documentType);
                          }}
                        >
                          {isExpanded ? '▼' : '▶'}
                        </button>
                      </td>
                    )}
                    {visibleColumns.map((column) => (
                      <td
                        key={column.key}
                        className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 ${column.class}`}
                      >
                        {formatCellValue(row[column.key], column.key, row, documentType)}
                      </td>
                    ))}
                  </tr>
                  
                  {/* Drill-down row */}
                  {isExpanded && renderDrillDownContent && showDrillDown && (
                    <tr>
                      <td colSpan={visibleColumns.length + (hasVatBooks ? 1 : 2) + (showCheckboxes ? 1 : 0)} className="px-0 py-0">
                        {renderDrillDownContent(rowId, documentType)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DynamicTable;
